import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { uc } from '../../unifiedContactsApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { ModuleHeader, TabBtn, Fld, SelectFld } from '../shared/formFields';
import ContactPicker from '../../shared/ContactPicker';
import QuickContactForm from '../../shared/QuickContactForm';
import { SsnField } from '../shared/SsnField';
import { EconsentStatus } from '../shared/EconsentStatus';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { AriveLockNote, AriveLocked, isAriveManaged, toastAriveConflict } from '../../ariveManaged';
import { DeclarationsEditor, DemographicsEditor } from '../../UrlaFullEditor';

export function BorrowerInfoModule({ loan, onChange }) {
  const [tab, setTab] = useState('basic');
  const [urla, setUrla] = useState(loan.urla || {});
  const [saving, setSaving] = useState(false);
  const [linkedContact, setLinkedContact] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [pendingContactId, setPendingContactId] = useState(loan.contact_id || null);
  const [savingContact, setSavingContact] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  useEffect(() => {
    setUrla(loan.urla || {});
  }, [loan]);
  useEffect(() => {
    let cancelled = false;
    setPendingContactId(loan.contact_id || null);
    setEditingContact(false);
    if (loan.contact_id) {
      uc.get(loan.contact_id)
        .then(contact => { if (!cancelled) setLinkedContact(contact); })
        .catch(() => { if (!cancelled) setLinkedContact(null); });
    } else {
      setLinkedContact(null);
    }
    return () => { cancelled = true; };
  }, [loan.contact_id]);
  const updB = (k, v) => setUrla(u => ({ ...u, borrower: { ...(u.borrower || {}), [k]: v } }));
  const updDecl = (d) => setUrla(u => ({ ...u, declarations: d }));
  const updDemo = (key, d) => setUrla(u => ({ ...u, [key]: d }));
  const updC = (k, v) => setUrla(u => ({ ...u, coborrower: { ...(u.coborrower || {}), [k]: v } }));
  const b = urla.borrower || {};
  const save = async () => {
    setSaving(true);
    try { await losApi.updateUrla(loan.loan_file_id, urla); toast({ title: 'Borrower info saved' }); onChange(); }
    catch (e) { if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) }); }
    finally { setSaving(false); }
  };

  const removeCoborrower = async () => {
    const ok = await confirmDialog({
      title: 'Remove the co-borrower?',
      description: 'Their details and demographic answers are dropped when you save. Their assets and liabilities stay on the application; review them after saving.',
      confirmText: 'Remove', destructive: true,
    });
    if (!ok) return;
    setUrla(u => ({ ...u, coborrower: null, demographics_coborrower: null }));
    setTab('basic');
  };

  // Known limitation: saving a contact link does not preserve unsaved edits
  // made to the 1003 fields above (urla state is not persisted first), so a
  // link change can silently discard in-progress edits. Needs dirty-tracking
  // to fix properly — out of scope for now.
  const saveContactLink = async (contactId) => {
    setSavingContact(true);
    try {
      await losApi.updateLoan(loan.loan_file_id, { contact_id: contactId });
      toast({ title: contactId ? 'Contact linked' : 'Contact removed' });
      setEditingContact(false);
      onChange();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e, 'Error') });
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Borrower Info" onSave={save} saving={saving} />
      <AriveLockNote loan={loan} testId="bi-arive-note">Borrower details, declarations and demographics are edited in Arive. The SSN stays editable here.</AriveLockNote>

      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="borrower-linked-contact-section">
        <div className="text-[13px] font-semibold mb-2" style={{ color: '#030524' }}>Linked Contact</div>
        {!editingContact ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[13px] text-slate-600">
              {loan.contact_id
                ? (linkedContact?.display_name || `Contact ${loan.contact_id}`)
                : 'No contact linked'}
            </div>
            <div className="flex gap-2">
              {loan.contact_id ? (
                <>
                  <button type="button" onClick={() => setEditingContact(true)} data-testid="borrower-change-contact-btn"
                    className="text-[12px] font-semibold text-slate-600 hover:text-slate-800">
                    Change
                  </button>
                  <button type="button" onClick={() => saveContactLink(null)} disabled={savingContact} data-testid="borrower-remove-contact-btn"
                    className="text-[12px] font-semibold text-red-600 hover:text-red-700">
                    Remove
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => setEditingContact(true)} data-testid="borrower-attach-contact-btn"
                  className="text-[12px] font-semibold" style={{ color: '#070B5F' }}>
                  Attach contact
                </button>
              )}
            </div>
          </div>
        ) : creatingContact ? (
          <QuickContactForm
            defaultType="borrower"
            testIdPrefix="borrower-new-contact"
            onCancel={() => setCreatingContact(false)}
            onCreated={(c) => { setCreatingContact(false); setPendingContactId(c.contact_id); saveContactLink(c.contact_id); }}
          />
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <ContactPicker value={pendingContactId} onChange={setPendingContactId} label="" placeholder="Select a contact…" testId="borrower-contact-picker"
                onCreateNew={() => setCreatingContact(true)} />
            </div>
            <button type="button" onClick={() => saveContactLink(pendingContactId)} disabled={savingContact}
              className="h-9 px-3 text-[12px] font-semibold rounded-lg text-white" style={{ background: '#070B5F' }}>
              {savingContact ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setEditingContact(false); setPendingContactId(loan.contact_id || null); }}
              className="h-9 px-3 text-[12px] font-semibold rounded-lg text-slate-600 hover:bg-slate-100">
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center border-b border-slate-100 px-2">
          <TabBtn active={tab === 'basic'} onClick={() => setTab('basic')}>Basic Details</TabBtn>
          <TabBtn active={tab === 'coborrower'} onClick={() => setTab('coborrower')}>Co-Borrower</TabBtn>
          <TabBtn active={tab === 'declarations'} onClick={() => setTab('declarations')}>Declarations</TabBtn>
          <TabBtn active={tab === 'demographics'} onClick={() => setTab('demographics')}>Demographics</TabBtn>
        </div>

        {tab === 'basic' && (
          <BorrowerDetails loan={loan} who="borrower" prefix="bi" b={b} updB={updB} />
        )}

        {tab === 'coborrower' && (urla.coborrower ? (
          <div>
            <BorrowerDetails loan={loan} who="coborrower" prefix="cb" b={urla.coborrower} updB={updC} />
            <div className="px-5 pb-5">
              <button type="button" onClick={removeCoborrower} data-testid="cb-remove" disabled={isAriveManaged(loan)}
                className="text-[12px] font-semibold text-red-600 hover:text-red-700 disabled:opacity-40">
                Remove co-borrower
              </button>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center">
            <div className="text-sm text-slate-500 mb-3">No co-borrower on this loan.</div>
            {/* The server keeps a stored co-borrower's SSN and e-consent until a save drops them,
                so a replacement can only be added after the removal is saved. */}
            <button type="button" onClick={() => setUrla(u => ({ ...u, coborrower: {} }))} data-testid="cb-add"
              disabled={!!loan.urla?.coborrower || isAriveManaged(loan)}
              className="text-[12px] font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-40" style={{ background: '#070B5F' }}>
              Add co-borrower
            </button>
            {loan.urla?.coborrower && (
              <div className="text-[11px] text-slate-400 mt-2" data-testid="cb-add-note">Save to finish removing the current co-borrower, then add the new one.</div>
            )}
          </div>
        ))}

        {tab === 'declarations' && (
          <div className="p-5" data-testid="bi-declarations">
            <AriveLocked loan={loan}><DeclarationsEditor decl={urla.declarations || {}} onChange={updDecl} /></AriveLocked>
          </div>
        )}

        {tab === 'demographics' && (
          <AriveLocked loan={loan}>
          <div className="p-5 space-y-6" data-testid="bi-demographics">
            <DemographicsEditor demo={urla.demographics_borrower || {}} onChange={(d) => updDemo('demographics_borrower', d)} label="Borrower" />
            {urla.coborrower && (
              <DemographicsEditor demo={urla.demographics_coborrower || {}} onChange={(d) => updDemo('demographics_coborrower', d)} label="Co-Borrower" />
            )}
          </div>
          </AriveLocked>
        )}
      </div>
    </div>
  );
}

// The Basic Details form for one applicant. `who` is the urla key; `prefix` keeps
// the test ids of the borrower (bi-) and co-borrower (cb-) forms apart.
function BorrowerDetails({ loan, who, prefix, b, updB }) {
  const age = b.dob ? Math.floor((new Date() - new Date(b.dob)) / (365.25 * 86400000)) : null;
  return (
    <div className="p-5 space-y-5">
      {/* Personal Info. The SSN is the one field Arive does not own, so its cell sits
          between two disabled fieldsets (a nested fieldset cannot re-enable it). */}
      <div>
        <div className="text-[13px] font-semibold mb-3" style={{ color: '#030524' }}>Personal Info</div>
        <div className="mb-3 space-y-2 text-[12px]">
          <EconsentStatus borrower={b} testId={`${prefix}-econsent`} />
          <label className="flex items-center gap-2">
            <input type="checkbox" disabled={isAriveManaged(loan)} checked={!!b.credit_pull_authorized} onChange={(e) => updB('credit_pull_authorized', e.target.checked)} data-testid={`${prefix}-credit-auth`} />
            Credit Pull Authorized
          </label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <AriveLocked loan={loan}>
          <Fld label="First Name" value={b.first_name} onChange={(v) => updB('first_name', v)} testId={`${prefix}-first`} />
          <Fld label="Middle Name" value={b.middle_name} onChange={(v) => updB('middle_name', v)} testId={`${prefix}-middle`} />
          <Fld label="Last Name" value={b.last_name} onChange={(v) => updB('last_name', v)} testId={`${prefix}-last`} />
          <Fld label="Suffix" value={b.suffix} onChange={(v) => updB('suffix', v)} />
          </AriveLocked>

          <SsnField loanId={loan.loan_file_id} who={who} value={b.ssn} onChange={(v) => updB('ssn', v)} testId={`${prefix}-ssn`} />
          <AriveLocked loan={loan}>
          <Fld label={`DOB${age !== null ? ` (age ${age})` : ''}`} type="date" value={b.dob?.substring(0, 10)} onChange={(v) => updB('dob', v)} testId={`${prefix}-dob`} />
          <SelectFld label="Residency Type" value={b.citizenship} onChange={(v) => updB('citizenship', v)}
            options={[['us_citizen', 'US Citizen'], ['permanent_resident', 'Permanent Resident'], ['non_permanent_resident', 'Non-Permanent Resident']]} />
          <SelectFld label="Marital Status" value={b.marital_status} onChange={(v) => updB('marital_status', v)}
            options={[['married', 'Married'], ['unmarried', 'Unmarried'], ['separated', 'Separated']]} />
          </AriveLocked>
        </div>
        <label className="flex items-center gap-2 text-[12px] mt-2">
          <input type="checkbox" disabled={isAriveManaged(loan)} checked={!!b.unmarried_partner} onChange={(e) => updB('unmarried_partner', e.target.checked)} data-testid={`${prefix}-unmarried-partner`} />
          Has unmarried partner
        </label>
      </div>

      {/* Contact */}
      <AriveLocked loan={loan}>
      <div>
        <div className="text-[13px] font-semibold mb-3" style={{ color: '#030524' }}>Contact</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Fld label="Email" value={b.email} onChange={(v) => updB('email', v)} testId={`${prefix}-email`} />
          <Fld label="Cell Phone" kind="phone" value={b.cell_phone} onChange={(v) => updB('cell_phone', v)} testId={`${prefix}-cell`} />
          <Fld label="Work Phone" kind="phone" value={b.work_phone} onChange={(v) => updB('work_phone', v)} />
          <Fld label="Ext" value={b.work_ext} onChange={(v) => updB('work_ext', v)} testId={`${prefix}-work-ext`} />
          <Fld label="Home Phone" kind="phone" value={b.home_phone} onChange={(v) => updB('home_phone', v)} />
          <Fld label="# Dependents" kind="number" value={b.dependents_count} onChange={(v) => updB('dependents_count', Number(v))} />
          <Fld label="Dependents Ages" value={b.dependents_ages} onChange={(v) => updB('dependents_ages', v)} placeholder="e.g. 4, 9, 12" />
          <Fld label="Est. Credit Score" kind="number" value={b.estimated_credit_score} onChange={(v) => updB('estimated_credit_score', Number(v))} testId={`${prefix}-score`} />
        </div>
      </div>
      </AriveLocked>

      {/* Address */}
      <AriveLocked loan={loan}>
      <div>
        <div className="text-[13px] font-semibold mb-3" style={{ color: '#030524' }}>Current Address</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2 md:col-span-4">
            <Fld label="Street" value={b.current_address?.street} onChange={(v) => updB('current_address', { ...(b.current_address || {}), street: v })} />
          </div>
          <Fld label="City" value={b.current_address?.city} onChange={(v) => updB('current_address', { ...(b.current_address || {}), city: v })} />
          <Fld label="State" value={b.current_address?.state} onChange={(v) => updB('current_address', { ...(b.current_address || {}), state: v.toUpperCase() })} />
          <Fld label="ZIP" value={b.current_address?.zip} onChange={(v) => updB('current_address', { ...(b.current_address || {}), zip: v })} />
          <SelectFld label="Housing" value={b.housing_type} onChange={(v) => updB('housing_type', v)}
            options={[['own', 'Own'], ['rent', 'Rent'], ['no_expense', 'No expense']]} />
        </div>
        <label className="flex items-center gap-2 text-[12px] mt-2">
          <input type="checkbox" checked={!!b.mailing_same} onChange={(e) => updB('mailing_same', e.target.checked)} data-testid={`${prefix}-mailing-same`} />
          Mailing address same as current
        </label>
      </div>
      </AriveLocked>

      {/* Military */}
      <AriveLocked loan={loan}>
      <div>
        <div className="text-[13px] font-semibold mb-3" style={{ color: '#030524' }}>Military Service</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SelectFld label="Currently Serving" value={b.military?.currently_serving ? 'yes' : 'no'} onChange={(v) => updB('military', { ...(b.military || {}), currently_serving: v === 'yes' })}
            options={[['no', 'No'], ['yes', 'Yes']]} />
          <SelectFld label="Veteran" value={b.military?.veteran ? 'yes' : 'no'} onChange={(v) => updB('military', { ...(b.military || {}), veteran: v === 'yes' })}
            options={[['no', 'No'], ['yes', 'Yes']]} />
        </div>
      </div>
      </AriveLocked>
    </div>
  );
}
